import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthShell, { GoogleButton } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";

export default function Register() {
  const { register, errMessage } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", institution: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      setSent(true);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="One more step to activate your account."
        footer={<>Already verified? <Link to="/login" className="font-medium text-[#0D9488] hover:underline">Log in</Link></>}
      >
        <div className="space-y-4" data-testid="register-sent">
          <div className="rounded-md bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
            If this email can be registered, we've sent a verification link to <strong>{form.email}</strong>.
            Click the link in that email to confirm your account, then log in.
          </div>
          <p className="text-sm text-slate-500">
            The link expires in 24 hours. Didn't get it? Check your spam folder, or{" "}
            <Link to="/login" className="font-medium text-[#0D9488] hover:underline">try logging in</Link> to resend it.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Save, edit, and organize your academic reference pages."
      footer={<>Already have an account? <Link to="/login" className="font-medium text-[#0D9488] hover:underline">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <div role="alert" data-testid="register-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="institution">Institution <span className="text-slate-400">(optional)</span></Label>
          <Input id="institution" value={form.institution} onChange={(e) => set("institution", e.target.value)} data-testid="register-institution" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" autoComplete="new-password" required value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" />
          <p className="text-xs text-slate-500">At least 8 characters.</p>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="register-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs text-slate-400">OR</span>
        <Separator className="flex-1" />
      </div>
      <GoogleButton label="Sign up with Google" />
    </AuthShell>
  );
}
